import React, {useEffect, useMemo, useState} from 'react';
import {Table, Button, Tooltip, Space, message} from 'antd';
import {PrinterOutlined, FileExcelOutlined} from '@ant-design/icons';
import {useParams} from 'react-router';
import {useDispatch} from 'react-redux';
import {fetchWorkOrderReport} from '../../slices/work-order';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

const PrintWorkOrderReport = () => {
  const {id} = useParams();
  const [orderLoading, setOrderLoading] = useState(false);
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [orderData, setOrderData] = useState([]);
  const [pagesize, setPageSize] = useState(20);
   const [summaryData, setSummaryData] = useState([]);

  const fetchWorkOrderData = (die_id = id) => {
    setOrderLoading(true);

    const params = {
      page,
      pagesize,
      die_id: die_id,
    };

    dispatch(fetchWorkOrderReport(params))
      .then(response => {
        setOrderData(response?.payload?.data || []);
        setSummaryData(response?.payload?.total_summary || []);
      })
      .catch(error => {
        console.error('Error fetching work order report:', error);
        setOrderData([]);
        message.error('Failed to fetch work order report');
      })
      .finally(() => {
        setOrderLoading(false);
      });
  };

  useEffect(() => {
    fetchWorkOrderData(id);
  }, [id]);

  const processTableData = data => {
    const sortedData = [...data].sort((a, b) =>
      a.customer_name.localeCompare(b.customer_name),
    );
    const customerGroups = {};
    sortedData.forEach((item, index) => {
      if (!customerGroups[item.customer_name]) {
        customerGroups[item.customer_name] = [];
      }
      customerGroups[item.customer_name].push({...item, originalIndex: index});
    });
    let processedData = [];
    Object.keys(customerGroups).forEach(customerName => {
      const customerItems = customerGroups[customerName];
      const workorderGroups = {};
      customerItems.forEach(item => {
        const key = `${item.workorder_no}_${item.po_no}_${item.section}`;
        if (!workorderGroups[key]) {
          workorderGroups[key] = [];
        }
        workorderGroups[key].push(item);
      });
      let customerStartIndex = processedData.length;
      let customerCount = 0;
      Object.keys(workorderGroups).forEach(groupKey => {
        const groupItems = workorderGroups[groupKey];
        const groupStartIndex = processedData.length;

        groupItems.forEach((item, index) => {
          processedData.push({
            ...item,
            customerSpanInfo:
              index === 0
                ? {
                    firstIndex: customerStartIndex,
                    count: customerItems.length,
                  }
                : null,
            groupSpanInfo:
              index === 0
                ? {
                    firstIndex: groupStartIndex,
                    count: groupItems.length,
                  }
                : null,
          });
        });

        customerCount += groupItems.length;
      });

      if (processedData[customerStartIndex]) {
        processedData[customerStartIndex].customerSpanInfo = {
          firstIndex: customerStartIndex,
          count: customerCount,
        };
      }
    });

    return processedData;
  };

  const processedData = useMemo(() => {
    return processTableData(orderData);
  }, [orderData]);

  const handleExcelDownload = () => {
    const sortedData = [...orderData].sort((a, b) => {
      return (
        a.customer_name.localeCompare(b.customer_name) ||
        a.workorder_no.localeCompare(b.workorder_no) ||
        a.po_no.localeCompare(b.po_no)
      );
    });

    // Calculate spans for customer_name
    const customerSpanMap = {};
    sortedData.forEach((item, index) => {
      const name = item.customer_name;
      if (!customerSpanMap[name]) {
        customerSpanMap[name] = {
          count: 1,
          firstIndex: index,
        };
      } else {
        customerSpanMap[name].count++;
      }
    });

    // Calculate spans for workorder_no and po_no
    const workOrderSpanMap = {};
    const poNoSpanMap = {};
    sortedData.forEach((item, index) => {
      // Work Order grouping
      const isWorkOrderRepeated =
        index > 0 && sortedData[index - 1].workorder_no === item.workorder_no;
      if (!isWorkOrderRepeated) {
        let count = 1;
        for (let i = index + 1; i < sortedData.length; i++) {
          if (sortedData[i].workorder_no === item.workorder_no) {
            count++;
          } else {
            break;
          }
        }
        workOrderSpanMap[item.workorder_no] = {
          count,
          firstIndex: index,
        };
      }

      // PO Number grouping
      const isPoNoRepeated =
        index > 0 &&
        sortedData[index - 1].workorder_no === item.workorder_no &&
        sortedData[index - 1].po_no === item.po_no;
      if (!isPoNoRepeated) {
        let count = 1;
        for (let i = index + 1; i < sortedData.length; i++) {
          if (
            sortedData[i].workorder_no === item.workorder_no &&
            sortedData[i].po_no === item.po_no
          ) {
            count++;
          } else {
            break;
          }
        }
        poNoSpanMap[`${item.workorder_no}|${item.po_no}`] = {
          count,
          firstIndex: index,
        };
      }
    });

    const wb = XLSX.utils.book_new();

    const wsData = [];

    const headers = [
      'Sr. No.',
      'Customer Name',
      'Work Order No / Date',
      'PO No / Date',
      'Profile No',
      'Customer Reference Number',
      'Length (mm)',
      'Order PCS',
      'Order KGS',
      'Packed PCS',
      'Packed KGS',
      'Warehouse PCS',
      'Warehouse KGS',
      'Dispatched PCS',
      'Dispatched KGS',
      'Pending PCS',
      'Pending KGS',
      'Total KG',
      'Remarks',
    ];

    wsData.push(headers);

    sortedData.forEach((order, index) => {
      const customerSpanInfo = customerSpanMap[order.customer_name];
      const showCustomerCell = customerSpanInfo.firstIndex === index;

      const isWorkOrderRepeated =
        index > 0 && sortedData[index - 1].workorder_no === order.workorder_no;
      const workOrderCell = isWorkOrderRepeated
        ? ''
        : `${order.workorder_no || ''}${order.order_date ? '\n' + dayjs(order.order_date).format('DD-MM-YYYY') : ''}`;

      const isPoNoRepeated =
        index > 0 &&
        sortedData[index - 1].workorder_no === order.workorder_no &&
        sortedData[index - 1].po_no === order.po_no;
      const poNoCell = isPoNoRepeated
        ? ''
        : `${order.po_no || ''}${order.po_date ? '\n' + dayjs(order.po_date).format('DD-MM-YYYY') : ''}`;

      const totalKgCell = `${order.total_kgs ? parseFloat(order.total_kgs).toFixed(3) : ''}`;

      const row = [
        index + 1,
        showCustomerCell ? order.customer_name || '' : '',
        workOrderCell,
        poNoCell,
        order.section || 'N/A',
        order.customer_reference_number || '',
        order.length || 'N/A',
        order.order_pcs || '',
        order.order_kgs ? parseFloat(order.order_kgs) : '',
        order.packed_pcs || '',
        order.packed_kgs ? parseFloat(order.packed_kgs) : '',
        order.warehouse_pcs || '',
        order.warehouse_kgs ? parseFloat(order.warehouse_kgs) : '',
        order.dispatched_pcs || '',
        order.dispatched_kgs ? parseFloat(order.dispatched_kgs) : '',
        order.pending_pcs || '',
        order.pending_kgs ? parseFloat(order.pending_kgs) : '',
        // order.order_kgs ? parseFloat(order.order_kgs).toFixed(3) : '',
        // order.dispatched_pcs || '',
        // order.dispatched_kgs ? parseFloat(order.dispatched_kgs).toFixed(3) : '',
        // order.packed_pcs || '',
        // order.packed_kgs ? parseFloat(order.packed_kgs).toFixed(3) : '',
        // order.warehouse_pcs || '',
        // order.warehouse_kgs ? parseFloat(order.warehouse_kgs).toFixed(3) : '',
        // order.pending_pcs || '',
        // order.pending_kgs ? parseFloat(order.pending_kgs).toFixed(3) : '',
        totalKgCell,
        order.remark || '',
      ];

      wsData.push(row);
    });

    if (sortedData.length > 0) {
      const totalRowIndex = wsData.length + 1; // Excel row index after data rows
      const totalRow = [
        '',
        '',
        '',
        '',
        '',
        '',
        'TOTAL',
        {f: `SUM(H2:H${totalRowIndex - 1})`}, // Order PCS (col H)
        {f: `SUM(I2:I${totalRowIndex - 1})`}, // Order KGS (col I)
        {f: `SUM(J2:J${totalRowIndex - 1})`}, // Dispatched PCS (col J)
        {f: `SUM(K2:K${totalRowIndex - 1})`}, // Dispatched KGS (col K)
        {f: `SUM(L2:L${totalRowIndex - 1})`}, // Packed PCS (col L)
        {f: `SUM(M2:M${totalRowIndex - 1})`}, // Packed KGS (col M)
        {f: `SUM(N2:N${totalRowIndex - 1})`}, // Warehouse PCS (col N)
        {f: `SUM(O2:O${totalRowIndex - 1})`}, // Warehouse KGS (col O)
        {f: `SUM(P2:P${totalRowIndex - 1})`}, // Pending PCS (col P)
        {f: `SUM(Q2:Q${totalRowIndex - 1})`}, // Pending KGS (col Q)
        '',
      ];

      wsData.push(totalRow);
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    const merges = [];

    // Merge cells for customer_name
    Object.entries(customerSpanMap).forEach(([customerName, spanInfo]) => {
      if (spanInfo.count > 1) {
        const startRow = spanInfo.firstIndex + 1;
        const endRow = startRow + spanInfo.count - 1;
        merges.push({
          s: {r: startRow, c: 1},
          e: {r: endRow, c: 1},
        });
      }
    });

    // Merge cells for workorder_no
    Object.entries(workOrderSpanMap).forEach(([workOrderNo, spanInfo]) => {
      if (spanInfo.count > 1) {
        const startRow = spanInfo.firstIndex + 1;
        const endRow = startRow + spanInfo.count - 1;
        merges.push({
          s: {r: startRow, c: 2},
          e: {r: endRow, c: 2},
        });
      }
    });

    // Merge cells for po_no
    Object.entries(poNoSpanMap).forEach(([key, spanInfo]) => {
      if (spanInfo.count > 1) {
        const startRow = spanInfo.firstIndex + 1;
        const endRow = startRow + spanInfo.count - 1;
        merges.push({
          s: {r: startRow, c: 3},
          e: {r: endRow, c: 3},
        });
      }
    });

    ws['!merges'] = merges;
    ['I', 'K', 'M', 'O', 'Q'].forEach(col => {
      for (let row = 2; row <= sortedData.length + 2; row++) {
        const cellAddress = `${col}${row}`;
        if (ws[cellAddress]) {
          ws[cellAddress].z = '0.000'; // 3 decimal places
        }
      }
    });

    const colWidths = [
      {wch: 8},
      {wch: 20},
      {wch: 18},
      {wch: 15},
      {wch: 12},
      {wch: 20},
      {wch: 12},
      {wch: 10},
      {wch: 10},
      {wch: 12},
      {wch: 12},
      {wch: 10},
      {wch: 10},
      {wch: 12},
      {wch: 12},
      {wch: 10},
      {wch: 10},
      {wch: 15},
    ];

    ws['!cols'] = colWidths;

    const borderStyle = {
      top: {style: 'thin', color: {rgb: '000000'}},
      bottom: {style: 'thin', color: {rgb: '000000'}},
      left: {style: 'thin', color: {rgb: '000000'}},
      right: {style: 'thin', color: {rgb: '000000'}},
    };

    const headerStyle = {
      font: {bold: true, color: {rgb: 'FFFFFF'}},
      fill: {fgColor: {rgb: '4F81BD'}},
      alignment: {horizontal: 'center', vertical: 'center'},
      border: borderStyle,
    };

    for (let col = 0; col < headers.length; col++) {
      const cellRef = XLSX.utils.encode_cell({r: 0, c: col});
      if (!ws[cellRef]) ws[cellRef] = {t: 's', v: headers[col]};
      ws[cellRef].s = headerStyle;
    }

    for (
      let row = 1;
      row < wsData.length - (sortedData.length > 0 ? 1 : 0);
      row++
    ) {
      for (let col = 0; col < headers.length; col++) {
        const cellRef = XLSX.utils.encode_cell({r: row, c: col});
        const cellStyle = {
          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true, // Enable wrap text for workorder_no and po_no cells with newlines
          },
          border: borderStyle,
        };

        if (ws[cellRef]) {
          ws[cellRef].s = cellStyle;
        } else {
          ws[cellRef] = {t: 's', v: '', s: cellStyle};
        }
      }
    }

    if (sortedData.length > 0) {
      const totalRowIndex = wsData.length - 1;
      for (let col = 0; col < headers.length; col++) {
        const cellRef = XLSX.utils.encode_cell({r: totalRowIndex, c: col});
        const totalRowStyle = {
          font: {bold: true},
          fill: {fgColor: {rgb: 'E7E6E6'}},
          alignment: {
            horizontal: 'center',
            vertical: 'center',
          },
          border: borderStyle,
        };

        if (ws[cellRef]) {
          ws[cellRef].s = totalRowStyle;
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, 'WorkOrder Report');

    const excelBuffer = XLSX.write(wb, {bookType: 'xlsx', type: 'array'});
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `Work_Order_Report_${sortedData[0]?.section || 'Profile'}_${dayjs().format('DD-MM-YYYY')}.xlsx`,
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const workOrderColumns = [
    {
      title: 'Sr. No.',
      dataIndex: 'sr_no',
      key: 'sr_no',
      render: (text, record, index) => index + 1,
      width: '2%',
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: '4%',
      render: (value, row, index) => {
        const customerSpanInfo = row.customerSpanInfo;
        const showCustomerCell =
          customerSpanInfo && customerSpanInfo.firstIndex === index;

        if (showCustomerCell) {
          return {
            children: value,
            props: {
              rowSpan: customerSpanInfo.count,
            },
          };
        }

        return {
          children: value,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: 'WorkOrder No / Date',
      dataIndex: 'workorder_no',
      key: 'workorder_no',
      width: '5%',
      render: (value, row, index) => {
        const groupSpanInfo = row.groupSpanInfo;
        const showGroupedCell =
          groupSpanInfo && groupSpanInfo.firstIndex === index;

        if (showGroupedCell) {
          return {
            children: value,
            props: {
              rowSpan: groupSpanInfo.count,
            },
          };
        }

        return {
          children: value,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: 'PO NO / Date',
      dataIndex: 'po_no',
      key: 'po_no',
      width: '5%',
      render: (value, row, index) => {
        const groupSpanInfo = row.groupSpanInfo;
        const showGroupedCell =
          groupSpanInfo && groupSpanInfo.firstIndex === index;

        if (showGroupedCell) {
          return {
            children: value,
            props: {
              rowSpan: groupSpanInfo.count,
            },
          };
        }

        return {
          children: value,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: 'Profile No',
      dataIndex: 'section',
      key: 'section',
      width: '4%',
      align: 'center',
      render: (value, row, index) => {
        const groupSpanInfo = row.groupSpanInfo;
        const showGroupedCell =
          groupSpanInfo && groupSpanInfo.firstIndex === index;

        if (showGroupedCell) {
          return {
            children: value,
            props: {
              rowSpan: groupSpanInfo.count,
            },
          };
        }

        return {
          children: value,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: 'Profile Image',
      dataIndex: 'profile_image',
      key: 'profile_image',
      width: '4%',
      align: 'center',
      render: (profile_image, row, index) => {
        const groupSpanInfo = row.groupSpanInfo;
        const showGroupedCell =
          groupSpanInfo && groupSpanInfo.firstIndex === index;

        if (showGroupedCell) {
          const imageContent =
            profile_image && profile_image.startsWith('https://') ? (
              <img
                width="48"
                src={profile_image}
                alt="Profile Image"
                style={{
                  width: 90,
                  height: 90,
                  objectFit: 'contain',
                  maxHeight: '100%',
                }}
              />
            ) : null;

          return {
            children: imageContent,
            props: {
              rowSpan: groupSpanInfo.count,
            },
          };
        }

        return {
          children: null,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: 'Customer Ref No',
      dataIndex: 'customer_reference_number',
      key: 'customer_reference_number',
      width: '3.5%',
      align: 'center',
    },
    {
      title: 'Length (mm)',
      dataIndex: 'length',
      key: 'length',
      width: '3.2%',
      align: 'center',
    },
    {
      title: 'Order',
      children: [
        {
          title: 'PCS',
          dataIndex: 'order_pcs',
          key: 'order_pcs',
          width: '3%',
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'order_kgs',
          key: 'order_kgs',
          width: '3%',
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Packed',
      children: [
        {
          title: 'PCS',
          dataIndex: 'packed_pcs',
          key: 'packed_pcs',
          width: '3%',
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'packed_kgs',
          key: 'packed_kgs',
          width: '3%',
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Warehouse',
      children: [
        {
          title: 'PCS',
          dataIndex: 'warehouse_pcs',
          key: 'warehouse_pcs',
          width: '3%',
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'warehouse_kgs',
          key: 'warehouse_kgs',
          width: '3%',
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Dispatched',
      children: [
        {
          title: 'PCS',
          dataIndex: 'dispatched_pcs',
          key: 'dispatched_pcs',
          width: '3%',
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'dispatched_kgs',
          key: 'dispatched_kgs',
          width: '3%',
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Pending',
      children: [
        {
          title: 'PCS',
          dataIndex: 'pending_pcs',
          key: 'pending_pcs',
          width: '3%',
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'pending_kgs',
          key: 'pending_kgs',
          width: '3%',
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Total KG',
      dataIndex: 'total_kgs',
      key: 'total_kgs',
      width: '2.8%',
      render: (value, row, index) => {
        const groupSpanInfo = row.groupSpanInfo;
        const showGroupedCell =
          groupSpanInfo && groupSpanInfo.firstIndex === index;

        if (showGroupedCell) {
          return {
            children: value,
            props: {
              rowSpan: groupSpanInfo.count,
            },
          };
        }

        return {
          children: value,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: 'Remarks',
      dataIndex: 'remark',
      key: 'remark',
      width: '5%',
      ellipsis: true,
      render: text => (
        <Tooltip title={text}>
          <span style={{whiteSpace: 'pre-wrap'}}>{text || ''}</span>
        </Tooltip>
      ),
    },
  ];

  const handlePrint = () => {
    // Add a small delay to ensure styles are applied
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleExportCSV = () => {
    const headers = [
      'Sr. No.',
      'Customer Name',
      'WorkOrder No',
      'PO No',
      'Profile No',
      'Customer Ref No',
      'Length (mm)',
      'Order PCS',
      'Order KGS',
      'Dispatched PCS',
      'Dispatched KGS',
      'Packed PCS',
      'Packed KGS',
      'Warehouse PCS',
      'Warehouse KGS',
      'Pending PCS',
      'Pending KGS',
      'Remarks',
    ];

    const csvData = processedData.map((row, index) => [
      index + 1,
      row.customer_name || '',
      row.workorder_no || '',
      row.po_no || '',
      row.section || '',
      row.customer_reference_number || '',
      row.length || '',
      row.order_pcs || '',
      row.order_kgs ? parseFloat(row.order_kgs).toFixed(3) : '',
      row.dispatched_pcs || '',
      row.dispatched_kgs ? parseFloat(row.dispatched_kgs).toFixed(3) : '',
      row.packed_pcs || '',
      row.packed_kgs ? parseFloat(row.packed_kgs).toFixed(3) : '',
      row.warehouse_pcs || '',
      row.warehouse_kgs ? parseFloat(row.warehouse_kgs).toFixed(3) : '',
      row.pending_pcs || '',
      row.pending_kgs ? parseFloat(row.pending_kgs).toFixed(3) : '',
      row.remark || '',
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `work_order_report_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{padding: '20px'}} className="print-container">
      <div
        className="print-header"
        style={{marginBottom: '20px', textAlign: 'center'}}
      >
        <h1>Work Order Report</h1>
        <h3>Profile No.: {orderData ? orderData[0]?.section : ''}</h3>
      </div>

      <div
        className="action-buttons"
        style={{marginBottom: '20px', textAlign: 'right'}}
      >
        <Space size="large">
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            size="large"
            loading={orderLoading}
          >
            Print
          </Button>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExcelDownload}
            size="large"
            loading={orderLoading}
          >
            Export Excel
          </Button>
        </Space>
      </div>

      <div className="table-wrapper">
        <Table
          dataSource={processedData}
          columns={workOrderColumns}
          pagination={false}
          rowKey="id" 
          bordered
          size="small"
          scroll={{x: 'max-content'}}
          summary={() => {
                      return (
                        <Table.Summary fixed>
                          <Table.Summary.Row style={{ fontWeight: 'bold' }}>
                            <Table.Summary.Cell index={0} colSpan={8}>
                              Total
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={6} align="center">
                              {summaryData?.order_pcs}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={7} align="right">
                              {summaryData?.order_kgs?.toFixed(3)}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={8} align="center">
                              {summaryData?.packed_pcs}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={9} align="right">
                              {summaryData?.packed_kgs?.toFixed(3)}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={10} align="center">
                              {summaryData?.warehouse_pcs}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={11} align="right">
                              {summaryData?.warehouse_kgs?.toFixed(3)}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={12} align="center">
                              {summaryData?.dispatched_pcs}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={13} align="right">
                              {summaryData?.dispatched_kgs?.toFixed(3)}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={14} align="center">
                              {summaryData?.pending_pcs}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={15} align="right">
                              {summaryData?.pending_kgs?.toFixed(3)}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={16}></Table.Summary.Cell>
                            <Table.Summary.Cell index={17}></Table.Summary.Cell>
                          </Table.Summary.Row>
                        </Table.Summary>
                      );
                    }}
        />
      </div>

      <style jsx global>{`
        @media print {
          /* Hide non-printable elements */
          .action-buttons,
          .ant-btn,
          .ant-tooltip,
          .ant-dropdown,
          .ant-menu {
            display: none !important;
          }

          /* Reset page margins and padding */
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          html,
          body {
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            overflow: visible !important;
          }

          /* Container styling */
          .print-container {
            padding: 10px !important;
            margin: 0 !important;
            width: 100% !important;
            max-width: none !important;
          }

          /* Header styling */
          .print-header {
            page-break-inside: avoid;
            margin-bottom: 15px !important;
            text-align: center;
          }

          .print-header h1 {
            margin: 0 0 8px 0 !important;
            font-size: 18px !important;
            font-weight: bold !important;
            color: #000 !important;
          }

          .print-header p {
            margin: 0 !important;
            font-size: 12px !important;
            color: #000 !important;
          }

          /* Table wrapper */
          .table-wrapper {
            width: 100% !important;
            overflow: visible !important;
          }

          /* Table styling */
          .ant-table {
            font-size: 12px !important;
            width: 100% !important;
            margin: 0 !important;
            page-break-inside: auto;
          }

          /* Table header styling */
          .ant-table-thead > tr > th {
            background: #12406a !important;
            color: white !important;
            padding: 4px 2px !important;
            font-size: 12px !important;
            font-weight: bold !important;
            text-align: center !important;
            page-break-inside: avoid;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Table body styling */
          .ant-table-tbody > tr > td {
            padding: 2px 1px !important;
            text-align: center !important;
            font-size: 12px !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            page-break-inside: avoid;
            border: 1px solid #999 !important;
          }

          /* Image styling in print */
          .ant-table-tbody img {
            max-width: 70px !important;
            max-height: 70px !important;
            object-fit: contain !important;
          }

          /* Remove table scroll */
          .ant-table-body {
            overflow: visible !important;
          }

          .ant-table .ant-table-container {
            overflow: visible !important;
          }

          /* Page break settings */
          .ant-table-tbody > tr {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          /* Ensure proper table layout */
          .ant-table-content {
            overflow: visible !important;
          }

          .ant-table-thead > tr > th {
            border: 1px #333 !important;
            box-shadow: none !important;
          }

          /* Force table to use full width */
          .ant-table-scroll {
            overflow: visible !important;
          }

          /* Remove any flex or grid layouts that might interfere */
          .ant-table-header,
          .ant-table-body,
          .ant-table-content {
            display: block !important;
          }

          /* Page settings */
          @page {
            size: A4 landscape;
            margin: 0.5cm;
          }
        }

        /* Screen styles for better visibility */
        .table-wrapper {
          overflow-x: auto;
        }

        .ant-table {
          min-width: 1200px;
        }
      `}</style>
    </div>
  );
};

export default PrintWorkOrderReport;
